"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ThemeToggle } from "@/components/theme-toggle";
import { useBreadcrumbTitle } from "@/components/breadcrumb-context";

const LABELS: Record<string, string> = {
  "": "XCORP",
  calendar: "Calendar",
  reports: "Cleaning reports",
  reservations: "Reservations",
  settings: "Settings",
  walkthroughs: "Walkthroughs",
  tasks: "Tasks",
};

const SINGULAR: Record<string, string> = {
  reports: "Cleaning report",
  reservations: "Reservation",
  walkthroughs: "Walkthrough",
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function titleize(seg: string) {
  if (LABELS[seg]) return LABELS[seg];
  return seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
}

export function SiteHeader() {
  const pathname = usePathname();
  const parts = pathname.split("/").filter(Boolean);

  return (
    <header className="sticky top-0 z-30 flex h-[calc(3rem+env(safe-area-inset-top))] shrink-0 items-center gap-2 rounded-t-xl border-b border-border bg-background/95 px-3 pt-[env(safe-area-inset-top)] backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <SidebarTrigger className="-ml-1" />
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem className="hidden sm:flex">
            {parts.length === 0 ? (
              <BreadcrumbPage>XCORP</BreadcrumbPage>
            ) : (
              <BreadcrumbLink asChild>
                <Link href="/">XCORP</Link>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          {parts.map((seg, i) => {
            const href = "/" + parts.slice(0, i + 1).join("/");
            const isLast = i === parts.length - 1;
            return <Crumb key={href} href={href} seg={seg} parent={parts[i - 1]} isLast={isLast} showSeparator={i > 0 || true} />;
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="ml-auto flex items-center gap-1">
        <ThemeToggle />
      </div>
    </header>
  );
}

function Crumb({
  href,
  seg,
  parent,
  isLast,
}: {
  href: string;
  seg: string;
  parent: string | undefined;
  isLast: boolean;
  showSeparator: boolean;
}) {
  const registered = useBreadcrumbTitle(href);
  let label = registered;
  if (!label) {
    if (UUID.test(seg) && parent && SINGULAR[parent]) label = SINGULAR[parent];
    else label = titleize(seg);
  }

  return (
    <>
      <BreadcrumbSeparator className="hidden sm:flex" />
      <BreadcrumbItem>
        {isLast ? (
          <BreadcrumbPage>{label}</BreadcrumbPage>
        ) : (
          <BreadcrumbLink asChild>
            <Link href={href}>{label}</Link>
          </BreadcrumbLink>
        )}
      </BreadcrumbItem>
    </>
  );
}
